import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  BarChart3, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  FileText, 
  LayoutDashboard, 
  Map, 
  Package, 
  ShieldCheck, 
  TrendingUp, 
  Users, 
  AlertCircle,
  Activity,
  ChevronDown,
  UserCheck,
  CalendarDays,
  MessageSquare
} from 'lucide-react';
import { Button, Badge, Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/ui';
import { useLocation } from 'wouter';

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const ACCENT_COLOR = "#0AB89E";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const SectionHeading = ({ badge, title, subtitle }: { badge: string, title: string, subtitle: string }) => (
  <div className="text-center max-w-3xl mx-auto mb-16 px-4">
    <Badge 
      variant="secondary" 
      className="mb-4 px-3 py-1"
      style={{ color: ACCENT_COLOR, backgroundColor: `${ACCENT_COLOR}10`, border: 'none' }}
    >
      {badge}
    </Badge>
    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-4" style={{ color: PRIMARY_BLUE }}>
      {title}
    </h2>
    <p className="text-lg text-gray-600 leading-relaxed">{subtitle}</p>
  </div>
);

const FeatureCard = ({ icon: Icon, title, description, delay = 0 }: { icon: any, title: string, description: string, delay?: number }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 0.5 }}
    className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
  >
    <div 
      className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-colors mb-4"
      style={{ backgroundColor: ACCENT_COLOR }}
    >
      <Icon className="w-6 h-6 text-white" />
    </div>
    <h3 className="text-xl font-normal mb-2" style={{ color: PRIMARY_BLUE }}>{title}</h3>
    <p className="text-gray-600 leading-relaxed">{description}</p>
  </motion.div>
);

export default function PracticeManagement() {
  const [, setLocation] = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBookDemo = () => {
    window.scrollTo(0, 0);
    setLocation("/book-demo");
  };

  const handleContact = () => {
    setLocation("/contact");
  };

  return (
    <div 
      className="min-h-screen bg-white font-sans"
      style={{
        backgroundImage: "url('/assets/shared/simple-bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        fontFamily: "Montserrat, system-ui, sans-serif",
      }}
    >
      {/* HERO SECTION */}
      <section className="relative pt-20 pb-32 md:pt-32 md:pb-48 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp}>
                <Badge className="mb-6 border-none" style={{ backgroundColor: `${PRIMARY_BLUE}20`, color: PRIMARY_BLUE }}>
                  <span className="relative flex h-2 w-2 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: PRIMARY_BLUE }}></span>
                    <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: PRIMARY_BLUE }}></span>
                  </span>
                  New: AI-Powered Practice Management
                </Badge>
              </motion.div>
              
              <motion.h1 
                variants={fadeInUp} 
                className="text-4xl lg:text-5xl font-normal leading-[1.1] mb-6"
                style={{ color: PRIMARY_BLUE }}
              >
                Operational Excellence,{' '}
                <span 
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, ${ACCENT_COLOR} 100%)`
                  }}
                >
                  Powered by AI
                </span>
              </motion.h1>
              
              <motion.p variants={fadeInUp} className="text-xl text-gray-600 mb-8 leading-relaxed max-w-lg">
                A unified platform that transforms how you manage capacity, staff, inventory, compliance, and finances—across every location, in real-time.
              </motion.p>
              
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto font-bold gap-2 rounded-full transition-all duration-200 hover:-translate-y-1 active:translate-y-0"
                  style={{
                    backgroundColor: PRIMARY_BLUE,
                    color: "white",
                    boxShadow: "0 4px 0 #0a2d4d, 0 6px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)",
                    border: "1px solid rgba(255,255,255,0.2)",
                  }}
                  onClick={handleBookDemo}
                >
                  Request a Demo 
                  <ArrowRight className="w-5 h-5" style={{ color: ACCENT_COLOR }} />
                </Button>
              </motion.div>

              <motion.div variants={fadeInUp} className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { label: "Operational Efficiency", value: "+40%", icon: TrendingUp },
                  { label: "Staffing Cost Reduction", value: "-30%", icon: Users },
                  { label: "Compliance Rate", value: "99.5%", icon: ShieldCheck },
                  { label: "Supply Cost Savings", value: "-25%", icon: Package },
                ].map((stat, i) => (
                  <div key={i} className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 mb-1" style={{ color: PRIMARY_BLUE }}>
                      <stat.icon className="w-5 h-5" />
                      <span className="text-xl font-normal">{stat.value}</span>
                    </div>
                    <span className="text-xs text-slate-500 font-medium leading-tight">{stat.label}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>
            
            {/* Abstract Dashboard Mockup Visualization */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative z-10">
                <img 
                  src="/assets/solutions/practice-management/3d_medical_command_center_dashboard_illustration.png" 
                  alt="Aiscendus Command Center Dashboard" 
                  className="rounded-2xl shadow-2xl border border-slate-200 w-full h-auto rotate-1 hover:rotate-0 transition-transform duration-700 ease-out"
                />
              </div>
              
              {/* Floating Elements */}
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                className="absolute -top-6 -right-6 bg-white p-4 rounded-xl shadow-xl border border-slate-100 z-20 w-48"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div 
                    className="p-2 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${ACCENT_COLOR}20`, color: ACCENT_COLOR }}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 font-medium">Compliance</div>
                    <div className="text-lg font-normal" style={{ color: PRIMARY_BLUE }}>99.5%</div>
                  </div>
                </div>
                <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                  <div className="h-full" style={{ backgroundColor: ACCENT_COLOR, width: "99.5%" }}></div>
                </div>
              </motion.div>

              <motion.div 
                animate={{ y: [0, 10, 0] }}
                transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 1 }}
                className="absolute -bottom-8 -left-8 bg-white p-4 rounded-xl shadow-xl border border-slate-100 z-20"
              >
                 <div className="flex gap-4 items-center">
                    <div className="text-center">
                      <div className="text-xs text-slate-400 uppercase tracking-wider font-normal mb-1">Wait Time</div>
                      <div className="text-2xl font-normal" style={{ color: PRIMARY_BLUE }}>12m</div>
                    </div>
                    <div className="h-8 w-px bg-slate-100"></div>
                    <div className="text-center">
                       <div className="text-xs text-slate-400 uppercase tracking-wider font-normal mb-1">Patients</div>
                       <div className="text-2xl font-normal" style={{ color: PRIMARY_BLUE }}>42</div>
                    </div>
                 </div>
              </motion.div>

              {/* Occupancy Bubble */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ repeat: Infinity, duration: 4.5, ease: "easeInOut", delay: 0.5 }}
                className="absolute top-1/3 -left-10 bg-white p-4 rounded-xl shadow-xl border border-slate-100 z-20 hidden md:block"
              >
                 <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 text-purple-600 rounded-lg">
                      <Users className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 font-medium">Occupancy</div>
                      <div className="text-lg font-normal" style={{ color: PRIMARY_BLUE }}>85%</div>
                    </div>
                 </div>
              </motion.div>

              {/* Inventory Bubble */}
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 5.5, ease: "easeInOut", delay: 1.5 }}
                className="absolute bottom-24 -right-10 bg-white p-4 rounded-xl shadow-xl border border-slate-100 z-20 hidden md:block"
              >
                 <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 text-orange-600 rounded-lg">
                      <Package className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 font-medium">Low Stock Alert</div>
                      <div className="text-sm font-bold" style={{ color: PRIMARY_BLUE }}>Flu Vaccines</div>
                    </div>
                 </div>
              </motion.div>

               {/* Role/Responsibility Bubble */}
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{ repeat: Infinity, duration: 6, ease: "easeInOut", delay: 2.5 }}
                className="absolute -top-12 left-12 bg-white p-3 rounded-xl shadow-xl border border-slate-100 z-20 hidden md:block"
              >
                 <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center border-2 border-white shadow-sm overflow-hidden">
                       <UserCheck className="w-6 h-6 text-slate-400" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 font-medium">On Duty</div>
                      <div className="text-sm font-bold" style={{ color: PRIMARY_BLUE }}>Dr. Emily Chen</div>
                      <div className="text-[10px] text-blue-600 font-medium">Head Physician</div>
                    </div>
                 </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* COMMAND CENTER SECTION */}
      <section className="py-24 bg-white/70">
        <div className="container mx-auto px-4">
          <SectionHeading 
            badge="Overview" 
            title="Real-Time Operational Intelligence" 
            subtitle="A command center dashboard that brings together KPIs, operational queues, risk alerts, and AI-powered recommendations." 
          />
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: LayoutDashboard, title: "KPI Deck", desc: "Real-time operational metrics with drill-down capability for instant performance visibility." },
              { icon: AlertCircle, title: "Risk & Deadlines", desc: "Proactive alerts for SLA breaches, expiring credentials, and compliance gaps." },
              { icon: Map, title: "Resource Slot Map", desc: "Visual grid of rooms, providers, and equipment availability to optimize utilization." },
              { icon: Package, title: "Inventory Supply Bar", desc: "Stock level indicators with reorder alerts to prevent stockouts." },
              { icon: FileText, title: "Documents & Consents", desc: "Pending signatures, missing documents, and consent tracking for compliance." },
              { icon: Activity, title: "Financial Pulse", desc: "Daily revenue, collections, and denial trends at a glance." },
            ].map((feature, i) => (
              <FeatureCard key={i} icon={feature.icon} title={feature.title} description={feature.desc} delay={i * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* CAPACITY MANAGEMENT SECTION */}
      <section className="py-24 bg-white/50 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge 
                variant="secondary" 
                className="mb-4 px-3 py-1"
                style={{ color: PRIMARY_BLUE, backgroundColor: `${PRIMARY_BLUE}10`, border: 'none' }}
              >
                Capacity & Utilization
              </Badge>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-6" style={{ color: PRIMARY_BLUE }}>Maximize Every Resource</h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                From site setup to real-time capacity boards, optimize room utilization, provider scheduling, and patient flow with AI-powered forecasting.
              </p>
              
              <div className="space-y-6">
                {[
                  { title: "Real-Time Capacity Board", desc: "Live view of room status, wait times, and patient queue." },
                  { title: "Surge & Diversion Console", desc: "Real-time surge alerts with auto-diversion rules." },
                  { title: "Forecast & What-If", desc: "AI-powered demand forecasting with scenario planning." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-normal text-sm"
                      style={{ backgroundColor: `${PRIMARY_BLUE}20`, color: PRIMARY_BLUE }}
                    >
                      {i + 1}
                    </div>
                    <div>
                      <h4 className="font-normal" style={{ color: PRIMARY_BLUE }}>{item.title}</h4>
                      <p className="text-gray-600 text-sm mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <motion.div
                 initial={{ opacity: 0, scale: 0.95 }}
                 whileInView={{ opacity: 1, scale: 1 }}
                 viewport={{ once: true }}
                 transition={{ duration: 0.8 }}
                 className="relative"
               >
                  <div 
                    className="absolute inset-0 blur-3xl rounded-full transform scale-90"
                    style={{ backgroundColor: `${PRIMARY_BLUE}05` }}
                  ></div>
                  <img 
                    src="/assets/solutions/practice-management/3d_isometric_medical_clinic_capacity_optimization_illustration.png" 
                    alt="Intelligent Capacity Management Floor Plan" 
                    className="relative z-10 rounded-2xl shadow-2xl border border-slate-200 w-full h-auto"
                  />
                  
                  {/* Bubble 1: Room Utilization */}
                  <motion.div 
                    animate={{ y: [0, -10, 0] }}
                    transition={{ repeat: Infinity, duration: 4, ease: "easeInOut", delay: 1 }}
                    className="absolute -bottom-6 -right-6 bg-white p-4 rounded-xl shadow-xl border border-slate-100 z-20"
                  >
                     <div className="flex items-center gap-3">
                        <div 
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: `${PRIMARY_BLUE}20`, color: PRIMARY_BLUE }}
                        >
                          <TrendingUp className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 font-medium">Room Utilization</div>
                          <div className="text-lg font-normal" style={{ color: PRIMARY_BLUE }}>94%</div>
                        </div>
                     </div>
                  </motion.div>

                  {/* Bubble 2: Patient Queue */}
                  <motion.div 
                    animate={{ y: [0, 8, 0] }}
                    transition={{ repeat: Infinity, duration: 4.5, ease: "easeInOut", delay: 0.5 }}
                    className="absolute top-20 -left-8 bg-white p-4 rounded-xl shadow-xl border border-slate-100 z-20"
                  >
                     <div className="flex items-center gap-3">
                        <div 
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: `${ACCENT_COLOR}20`, color: ACCENT_COLOR }}
                        >
                          <Users className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 font-medium">Patient Queue</div>
                          <div className="text-lg font-normal" style={{ color: PRIMARY_BLUE }}>8 waiting</div>
                        </div>
                     </div>
                  </motion.div>

                  {/* Bubble 3: Equipment Status */}
                  <motion.div 
                    animate={{ y: [0, -8, 0] }}
                    transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 2 }}
                    className="absolute top-10 right-20 bg-white p-4 rounded-xl shadow-xl border border-slate-100 z-20 hidden lg:block"
                  >
                     <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                          <Activity className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 font-medium">Equipment</div>
                          <div className="text-sm font-bold" style={{ color: PRIMARY_BLUE }}>All Available</div>
                        </div>
                     </div>
                  </motion.div>

                  {/* Bubble 4: Provider Availability */}
                  <motion.div 
                    animate={{ x: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 5.5, ease: "easeInOut", delay: 1.2 }}
                    className="absolute bottom-1/3 -left-12 bg-white p-4 rounded-xl shadow-xl border border-slate-100 z-20 hidden lg:block"
                  >
                     <div className="flex items-center gap-3">
                        <div 
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: `${ACCENT_COLOR}20`, color: ACCENT_COLOR }}
                        >
                          <UserCheck className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 font-medium">Providers On-Duty</div>
                          <div className="text-lg font-normal" style={{ color: PRIMARY_BLUE }}>6 of 8</div>
                        </div>
                     </div>
                  </motion.div>

                  {/* Bubble 5: Throughput Rate */}
                  <motion.div 
                    animate={{ y: [0, 10, 0] }}
                    transition={{ repeat: Infinity, duration: 4.8, ease: "easeInOut", delay: 0.8 }}
                    className="absolute top-1/2 -right-10 bg-white p-4 rounded-xl shadow-xl border border-slate-100 z-20 hidden lg:block"
                  >
                     <div className="flex items-center gap-3">
                        <div 
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: `${PRIMARY_BLUE}20`, color: PRIMARY_BLUE }}
                        >
                          <Clock className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 font-medium">Avg Throughput</div>
                          <div className="text-lg font-normal" style={{ color: PRIMARY_BLUE }}>45 min</div>
                        </div>
                     </div>
                  </motion.div>

                  {/* Bubble 6: Resource Efficiency */}
                  <motion.div 
                    animate={{ x: [0, 8, 0], y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 6, ease: "easeInOut", delay: 3 }}
                    className="absolute bottom-16 left-16 bg-white p-4 rounded-xl shadow-xl border border-slate-100 z-20 hidden lg:block"
                  >
                     <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                          <BarChart3 className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 font-medium">Efficiency Score</div>
                          <div className="text-lg font-normal" style={{ color: PRIMARY_BLUE }}>92%</div>
                        </div>
                     </div>
                  </motion.div>
               </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* STAFFING & SCHEDULING SECTION */}
      <section className="py-24 bg-white/70">
        <div className="container mx-auto px-4">
          <SectionHeading
            badge="Staffing & Scheduling"
            title="AI-Powered Workforce Scheduling"
            subtitle="From shift templates to demand forecasting, create optimal schedules that balance staff preferences, credentials, and patient volume—automatically."
          />
          
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column: Features List */}
            <div className="lg:col-span-1 space-y-4">
               {[
                 { title: "Multi-Clinic Calendar", desc: "Unified view across all locations.", icon: CalendarDays },
                 { title: "Demand Forecast", desc: "AI prediction of patient volume.", icon: TrendingUp },
                 { title: "Assignment Constraints", desc: "Define overtime limits & skill rules.", icon: CheckCircle2 },
                 { title: "Credential Checks", desc: "Block scheduling if expired.", icon: ShieldCheck },
               ].map((item, i) => (
                 <div key={i} className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex gap-4 items-start hover:border-blue-200 transition-colors cursor-default">
                   <div 
                     className="p-2 rounded-lg"
                     style={{ backgroundColor: `${ACCENT_COLOR}20`, color: ACCENT_COLOR }}
                   >
                     <item.icon className="w-5 h-5" />
                   </div>
                   <div>
                     <h4 className="font-normal text-sm" style={{ color: PRIMARY_BLUE }}>{item.title}</h4>
                     <p className="text-gray-600 text-xs mt-1">{item.desc}</p>
                   </div>
                 </div>
               ))}
               
               <div 
                 className="mt-8 p-6 rounded-xl text-white"
                 style={{ backgroundColor: PRIMARY_BLUE }}
               >
                  <div className="flex items-center gap-2 mb-4">
                     <Users className="w-5 h-5" style={{ color: ACCENT_COLOR }} />
                     <span className="font-normal">Optimization Impact</span>
                  </div>
                  <div className="space-y-4">
                     <div>
                       <div className="flex justify-between text-sm mb-1 text-slate-300">Schedule Fill Rate</div>
                       <div className="flex items-center gap-2">
                         <div className="h-2 flex-1 bg-blue-900/30 rounded-full overflow-hidden">
                           <div className="h-full bg-white w-[98%]"></div>
                         </div>
                         <span className="font-normal">98%</span>
                       </div>
                     </div>
                     <div>
                       <div className="flex justify-between text-sm mb-1 text-slate-300">Overtime Reduction</div>
                       <div className="flex items-center gap-2">
                         <div className="h-2 flex-1 bg-blue-900/30 rounded-full overflow-hidden">
                           <div className="h-full w-[40%]" style={{ backgroundColor: ACCENT_COLOR }}></div>
                         </div>
                         <span className="font-normal">-40%</span>
                       </div>
                     </div>
                  </div>
               </div>
            </div>
            
            {/* Right Column: Visual Mockup */}
            <div className="lg:col-span-2">
               <div className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden h-full flex flex-col">
                  <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                     <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                          <ChevronDown className="rotate-90 w-4 h-4" />
                        </Button>
                        <span className="font-normal text-slate-700">October 2025</span>
                        <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                          <ChevronDown className="-rotate-90 w-4 h-4" />
                        </Button>
                     </div>
                     <div className="flex gap-2">
                        <Badge variant="outline" className="bg-white">Day</Badge>
                        <Badge variant="outline" className="bg-white">Week</Badge>
                        <Badge style={{ backgroundColor: PRIMARY_BLUE, color: "white" }}>Month</Badge>
                     </div>
                  </div>
                  <div className="p-4 flex-1 overflow-auto">
                     {/* Mock Calendar Grid */}
                     <div className="grid grid-cols-7 gap-px bg-slate-100 border border-slate-100 rounded-lg overflow-hidden">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                          <div key={d} className="bg-slate-50 p-2 text-center text-xs font-normal text-slate-500">{d}</div>
                        ))}
                        {Array.from({ length: 28 }).map((_, i) => (
                           <div key={i} className="bg-white h-24 p-1 relative hover:bg-slate-50 transition-colors">
                              <span className="text-xs text-slate-400 p-1">{i + 1}</span>
                              {i % 3 === 0 && (
                                <div 
                                  className="mx-1 mt-1 p-1 text-[10px] rounded font-medium truncate border-l-2"
                                  style={{ 
                                    backgroundColor: `${PRIMARY_BLUE}10`, 
                                    color: PRIMARY_BLUE,
                                    borderColor: PRIMARY_BLUE 
                                  }}
                                >
                                   Dr. Sarah (AM)
                                </div>
                              )}
                              {i % 2 === 0 && (
                                 <div 
                                   className="mx-1 mt-1 p-1 text-[10px] rounded font-medium truncate border-l-2"
                                   style={{ 
                                     backgroundColor: `${ACCENT_COLOR}10`, 
                                     color: ACCENT_COLOR,
                                     borderColor: ACCENT_COLOR 
                                   }}
                                 >
                                   RN Mike (PM)
                                 </div>
                              )}
                              {i === 12 && (
                                 <div className="absolute bottom-1 right-1">
                                    <Badge variant="destructive" className="h-4 px-1 text-[10px]">Gap</Badge>
                                 </div>
                              )}
                           </div>
                        ))}
                     </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINANCIAL PERFORMANCE SECTION */}
      <section className="py-24 bg-white/50">
        <div className="container mx-auto px-4">
          <SectionHeading 
            badge="Revenue Intelligence" 
            title="Complete Financial Visibility" 
            subtitle="Track revenue, analyze costs, monitor denials, and optimize payer mix—all from a unified financial dashboard." 
          />
          
          <div className="grid lg:grid-cols-3 gap-6">
            {[
              { title: "Total Revenue (Today)", value: "$12,500", change: "+12.5% from yesterday", icon: TrendingUp },
              { title: "Denial Rate", value: "6.2%", change: "-0.8% improvement", icon: ArrowRight },
              { title: "Per Provider Avg", value: "$4,167", change: "Target: $4,000", icon: BarChart3 },
              { title: "Collections Rate", value: "94.5%", change: "+2.1% this month", icon: CheckCircle2 },
              { title: "Days in A/R", value: "32 days", change: "-5 days improvement", icon: Clock },
              { title: "Payer Mix", value: "Optimized", change: "Commercial 65%", icon: Users },
            ].map((stat, i) => (
              <Card 
                key={i} 
                className="border-none shadow-xl text-white"
                style={{ backgroundColor: PRIMARY_BLUE }}
              >
                 <CardHeader className="pb-2">
                   <CardTitle className="text-sm font-medium text-blue-100">{stat.title}</CardTitle>
                 </CardHeader>
                 <CardContent>
                   <div className="text-4xl font-normal text-white mb-2">{stat.value}</div>
                   <div className="flex items-center text-sm text-blue-100">
                     <stat.icon className="w-4 h-4 mr-1" /> {stat.change}
                   </div>
                 </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE SECTION */}
      <section className="py-24 bg-white/70">
        <div className="container mx-auto px-4">
          <div 
            className="rounded-3xl p-8 md:p-12 overflow-hidden relative"
            style={{ backgroundColor: PRIMARY_BLUE }}
          >
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            
            <div className="grid md:grid-cols-2 gap-12 relative z-10 text-white items-center">
               <div>
                 <Badge 
                   className="mb-6 border-none"
                   style={{ backgroundColor: "rgba(255,255,255,0.2)", color: "white" }}
                 >
                   Compliance Management
                 </Badge>
                 <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-6">Proactive Compliance, Built-In</h2>
                 <p className="text-blue-100 text-lg mb-8 leading-relaxed">
                   From HIPAA to OSHA, manage policies, track training, monitor risks, and maintain audit-ready documentation—automatically.
                 </p>
                 <Button 
                   size="lg" 
                   variant="secondary" 
                   className="font-bold"
                   style={{ color: PRIMARY_BLUE }}
                 >
                   Explore Compliance Features
                 </Button>
               </div>
               <div className="rounded-2xl backdrop-blur-md border border-white/20 shadow-lg p-6 bg-white/10">
                  <h3 className="text-xl font-normal mb-4 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5" /> Compliance Health
                  </h3>
                  <div className="space-y-4">
                    {[
                      { label: "HIPAA Controls", status: "Compliant", color: ACCENT_COLOR },
                      { label: "Staff Training", status: "98% Complete", color: ACCENT_COLOR },
                      { label: "Risk Assessments", status: "Review Needed", color: "#fbbf24" },
                      { label: "Policy Acknowledgments", status: "Up to date", color: ACCENT_COLOR },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-blue-900/30 rounded-lg">
                        <span className="font-medium text-blue-50">{item.label}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }}></div>
                          <span className="text-sm text-blue-100">{item.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center bg-white/90 backdrop-blur-sm rounded-xl p-8 shadow-sm">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-6" style={{ color: PRIMARY_BLUE }}>
              Ready to Transform Your Practice Operations?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Join leading practices that have improved operational efficiency by 40% with Aiscendus.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="w-full sm:w-auto font-bold gap-2 rounded-full transition-all duration-200 hover:-translate-y-1 active:translate-y-0"
                style={{
                  backgroundColor: PRIMARY_BLUE,
                  color: "white",
                  boxShadow: "0 4px 0 #0a2d4d, 0 6px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
                onClick={handleBookDemo}
              >
                Request a Demo
                <ArrowRight className="w-5 h-5" style={{ color: ACCENT_COLOR }} />
              </Button>
              <Button 
                size="lg" 
                className="w-full sm:w-auto gap-2 font-bold rounded-full transition-all duration-200 hover:-translate-y-1 active:translate-y-0"
                style={{
                  backgroundColor: "white",
                  color: PRIMARY_BLUE,
                  boxShadow: "0 4px 0 rgba(0,0,0,0.1), 0 6px 20px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.2)",
                  border: `1px solid ${PRIMARY_BLUE}40`,
                }}
                onClick={handleContact}
              >
                <MessageSquare className="w-5 h-5" style={{ color: ACCENT_COLOR }} />
                Contact Sales
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
